import React, { useEffect, useState } from "react";
import { deleteUser } from "../../api/userApi";
import { useJWT } from "../../contexts/JWTContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../contexts/ToastContext";
import apiClient from "../../api/apiClient";

import {
  Trophy,
  Target,
  Zap,
  Award,
  Settings,
  X,
  ChevronRight,
} from "lucide-react";

const KAKAO_REST_API_KEY = process.env.REACT_APP_KAKAO_REST_API_KEY;
const KAKAO_LOGOUT_REDIRECT_URI = "https://wingstep.shop/";

function GetUser() {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [avatarList, setAvatarList] = useState([]);

  const { user, isLoading, logout } = useJWT();
  const { showToast } = useToast();

  const navigate = useNavigate();

  const getSnsType = () => {
    return user?.snsType || localStorage.getItem("snsType") || "normal";
  };

  const clearAuthStorage = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("snsType");
  };

  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const res = await apiClient.get("/user/avatar/list");
        setAvatarList(res.data);
      } catch (e) {
        console.error("아바타 목록 조회 실패", e);
      }
    };
    fetchAvatars();
  }, []);

  const handleLogout = () => {
    const snsType = getSnsType();

    logout();
    clearAuthStorage();

    if (snsType === "kakao" && KAKAO_REST_API_KEY) {
      const kakaoLogoutUrl =
        "https://kauth.kakao.com/oauth/logout" +
        `?client_id=${KAKAO_REST_API_KEY}` +
        `&logout_redirect_uri=${encodeURIComponent(KAKAO_LOGOUT_REDIRECT_URI)}`;

      window.location.href = kakaoLogoutUrl;
      return;
    }

    showToast("로그아웃 되었습니다.", "i");
    navigate("/");
  };

  const handleDeleteUser = async () => {
    const confirmed = window.confirm(
      "정말 탈퇴하시겠습니까?\n탈퇴 시 정보가 모두 삭제되며, 사용하던 아이디로는 재가입이 불가합니다."
    );

    if (!confirmed) return;

    try {
      setIsDeleting(true);
      setShowSettingsModal(false);

      await deleteUser();

      showToast("회원탈퇴가 완료되었습니다.", "i");

      setTimeout(() => {
        logout();
        clearAuthStorage();
        navigate("/");
      }, 1200);
    } catch (err) {
      console.error(err);
      showToast("회원탈퇴 처리 중 오류가 발생했습니다.", "e");
      setIsDeleting(false);
    }
  };

  // 레벨별 아이콘 (10단계)
  const levelIconMap = {
    1: "level_1",
    2: "level_2",
    3: "level_3",
    4: "level_4",
    5: "level_5",
    6: "level_6",
    7: "level_7",
    8: "level_8",
    9: "level_9",
    10: "level_10",
  };

  const levelExpMap = {
    1: 0,
    2: 1_000,
    3: 3_000,
    4: 5_000,
    5: 10_000,
    6: 30_000,
    7: 50_000,
    8: 100_000,
    9: 500_000,
    10: 1_000_000,
  };

  const currentLevel = user?.levelId || 1; // 현재 레벨
  const currentXP = user?.exp || 0; // 현재 경험치

  const nextLevelXP =
    levelExpMap[currentLevel + 1] ?? levelExpMap[currentLevel];

  const progressPercent =
    currentLevel >= 10 ? 100 : Math.min((currentXP / nextLevelXP) * 100, 100);

  const formatExp = (num) => {
    if (!num || num < 1000) return num?.toString() || "0";

    // k 단위
    if (num < 1_000_000) {
      const result = (num / 1000).toFixed(1);
      return result.endsWith(".0") ? result.slice(0, -2) + "k" : result + "k";
    }

    // M 단위
    const result = (num / 1_000_000).toFixed(1);
    return result.endsWith(".0") ? result.slice(0, -2) + "M" : result + "M";
  };

  // 프로필 사진 옵션 (4개) - 목표 달성시 해금
  const avatarMap = {
    1: "avatar_basic",
    2: "avatar_runner",
    3: "avatar_pro",
    4: "avatar_master",
  };

  const safeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-7 w-24 bg-gray-200 rounded" />
          <div className="h-40 bg-gray-200 rounded-3xl" />
          <div className="h-28 bg-gray-200 rounded-3xl" />
          <div className="h-28 bg-gray-200 rounded-3xl" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        로그인 정보가 없습니다.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white px-4 py-6">
      {isDeleting && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="rounded-2xl bg-white px-6 py-5 shadow-xl flex items-center gap-4">
            <div className="h-6 w-6 rounded-full border-4 border-gray-300 border-t-gray-900 animate-spin" />
            <div>
              <p className="text-sm font-semibold text-gray-900">
                회원탈퇴 처리 중…
              </p>
              <p className="text-xs text-gray-500 mt-1">잠시만 기다려주세요.</p>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className="flex items-center justify-between mb-6">
        <div className="w-8"></div>
        <h1 className="text-2xl font-bold text-gray-800">내정보</h1>
        <button
          onClick={() => setShowSettingsModal(true)}
          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <Settings className="w-6 h-6" />
        </button>
      </header>

      {/* 프로필 카드 */}
      <section className="bg-white rounded-3xl shadow-lg p-6 mb-5 border-2 border-gray-100 relative">
        {/* 성별 배지 */}
        {(() => {
          const g = (user?.gender ?? "").trim();
          const isMale = g === "0" || g.toUpperCase() === "M";
          const isFemale = g === "1" || g.toUpperCase() === "F";

          if (!isMale && !isFemale) return null;

          return (
            <div className="absolute top-4 right-4">
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-lg shadow-sm ${
                  isMale
                    ? "text-blue-600 bg-blue-50"
                    : "text-pink-600 bg-pink-50"
                }`}
              >
                {isMale ? "MALE" : "FEMALE"}
              </span>
            </div>
          );
        })()}

        <div className="flex flex-col items-center">
          {/* 프로필 이미지 */}
          <div className="relative mb-4">
            <div className="w-24 h-24 rounded-full shadow-lg overflow-hidden">
              <img
                src={`/images/avatar/${
                  avatarMap[user?.avatarId] || "avatar_basic"
                }.png`}
                alt="프로필 아바타"
                className="w-full h-full object-contain scale-150"
              />
            </div>
            {/* 레벨 뱃지 */}
            <div className="absolute -bottom-1 -right-1 w-10 h-10 flex items-center justify-center">
              <img
                src={`/images/levelIcon/${
                  levelIconMap[user?.levelId] || "level_1"
                }.png`}
                alt="레벨 아이콘"
                className="w-full h-full object-cover scale-125"
              />
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-800 mb-1">
            {user ? user.nickname : ""}
          </h2>

          <p className="text-sm text-gray-500 mb-3">
            {user ? user.userId : ""}
          </p>
          <p className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
            {user?.regDate} 가입
          </p>
        </div>
      </section>

      {/* 레벨 진행상황 */}
      <section className="bg-gradient-to-br from-yellow-400 to-orange-400 rounded-3xl shadow-lg p-6 mb-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" fill="currentColor" />
            <span className="font-bold text-lg">Lv. {currentLevel}</span>
          </div>
          <span className="text-sm font-semibold">
            {formatExp(currentXP)} / {formatExp(nextLevelXP)}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-4 bg-white bg-opacity-30 rounded-full overflow-hidden">
          <div
            className="h-full bg-white rounded-full transition-all duration-300 shadow-inner"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </section>

      {/* 레벨 아이콘 컬렉션 */}
      <section className="bg-white rounded-3xl shadow-lg p-6 mb-5 border-2 border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-5 h-5 text-purple-500" />
          <h3 className="font-bold text-gray-800">레벨 배지</h3>
        </div>

        <div className="grid grid-cols-5 gap-3">
          {Object.keys(levelIconMap).map((key) => {
            const levelNum = Number(key);

            return (
              <div key={levelNum} className="flex flex-col items-center gap-1">
                <div
                  className={`w-12 h-12 rounded-2xl overflow-hidden shadow-md transition-all
                  ${
                    levelNum <= user.levelId
                      ? "scale-100"
                      : "scale-90 opacity-30"
                  }`}
                >
                  <img
                    src={`/images/levelIcon/${levelIconMap[levelNum]}.png`}
                    alt={`레벨 ${levelNum}`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {levelNum === user.levelId && (
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mt-1" />
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 프로필 사진 컬렉션 */}
      <section className="bg-white rounded-3xl shadow-lg p-6 mb-5 border-2 border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-bold text-gray-800">아바타 컬렉션</h3>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {avatarList.map((avatar) => {
            const unlocked = user.levelId >= avatar.reqLevel;

            return (
              <div key={avatar.avatarId} className="flex flex-col items-center">
                <div
                  className={`relative w-16 h-16 rounded-2xl overflow-hidden shadow-md
                    ${unlocked ? "" : "opacity-30"}`}
                >
                  <img
                    src={`/images/avatar/${avatar.avatarImg}`} // UpdateUser에서 쓰던 필드
                    alt={`아바타 ${avatar.avatarId}`}
                    className="w-full h-full object-contain scale-[1.8]"
                  />
                  {!unlocked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="text-2xl">🔒</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 전적 카드 */}
      <section className="bg-white rounded-3xl shadow-lg p-6 mb-5 border-2 border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-blue-500" />
          <h3 className="font-bold text-gray-800">경기 전적</h3>
        </div>

        {/* 개인전 */}
        <div className="mb-4 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-semibold text-gray-700">
                개인전
              </span>
            </div>
            <span className="text-xs text-gray-500">
              총{" "}
              {safeNumber(user.soloWinCount) + safeNumber(user.soloLoseCount)}
              경기
            </span>
          </div>

          <div className="flex gap-2">
            <div className="flex-1 bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {safeNumber(user.soloWinCount)}
              </p>
              <p className="text-xs text-gray-600 mt-1">승리</p>
            </div>
            <div className="flex-1 bg-red-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-red-600">
                {safeNumber(user.soloLoseCount)}
              </p>
              <p className="text-xs text-gray-600 mt-1">패배</p>
            </div>
          </div>
        </div>

        {/* 팀전 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-semibold text-gray-700">팀전</span>
            </div>
            <span className="text-xs text-gray-500">
              총{" "}
              {safeNumber(user.teamWinCount) + safeNumber(user.teamLoseCount)}
              경기
            </span>
          </div>

          <div className="flex gap-2">
            <div className="flex-1 bg-purple-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-purple-600">
                {safeNumber(user.teamWinCount)}
              </p>
              <p className="text-xs text-gray-600 mt-1">승리</p>
            </div>
            <div className="flex-1 bg-red-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-red-600">
                {safeNumber(user.teamLoseCount)}
              </p>
              <p className="text-xs text-gray-600 mt-1">패배</p>
            </div>
          </div>
        </div>
      </section>

      {/* 설정 바텀시트 모달 */}
      {showSettingsModal && (
        <>
          {/* 반투명 배경 */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowSettingsModal(false)}
          />

          {/* 바텀시트 */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 animate-slide-up">
            <div className="p-6">
              {/* 헤더 */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">설정</h2>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* 리스트 형태 */}
              <div className="space-y-1">
                {/* 프로필 수정 */}
                <button
                  onClick={() => {
                    setShowSettingsModal(false);
                    navigate("/update");
                  }}
                  className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <span className="text-gray-800 font-medium">프로필 수정</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                {/* 구분선 */}
                <div className="border-t border-gray-100"></div>

                {/* 로그아웃 */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <span className="text-gray-800 font-medium">로그아웃</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                {/* 구분선 */}
                <div className="border-t border-gray-100"></div>

                {/* 회원탈퇴 */}
                <button
                  onClick={handleDeleteUser}
                  className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-red-50 transition-colors"
                >
                  <span className="text-red-500 font-medium">회원탈퇴</span>
                  <ChevronRight className="w-5 h-5 text-red-300" />
                </button>
              </div>

              {/* 하단 여백 (안전 영역) */}
              <div className="h-6"></div>
            </div>
          </div>
        </>
      )}

      {/* 하단 여백 */}
      <div className="pb-24"></div>
    </div>
  );
}

export default GetUser;
